import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Avatar,
  Alert,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import {
  EmojiEvents as AwardIcon,
  Visibility as EyeIcon,
  Person as UserIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";

interface EmployeeMilestone {
  id: number;
  employeeName: string;
  employeeId: string;
  joinDate: string;
  completedMilestones: number;
  totalMilestones: number;
  progress: number;
  nextMilestone: string;
  nextMilestoneDate: string;
  currentPhase: string;
  daysEmployed: number;
}

const MilestonesTab: React.FC = () => {
  const theme = useTheme();
  const [successMessage, setSuccessMessage] = useState("");
  const [employees, setEmployees] = useState<EmployeeMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchEmployeeMilestones();
    const interval = setInterval(fetchEmployeeMilestones, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchEmployeeMilestones = async () => {
    try {
      const response = await axios.get("/api/milestones/all");
      setEmployees(response.data);
      setError("");
    } catch (err: any) {
      console.error("Error fetching employee milestones:", err);
      setError(err.response?.data?.message || "Failed to load employee milestones");
    } finally {
      setLoading(false);
    }
  };

  const handleViewTimeline = (id: number) => {
    const employee = employees.find(emp => emp.id === id);
    console.log(`Viewing full timeline for ${employee?.employeeName}`);
    setSuccessMessage(`Opening timeline for ${employee?.employeeName}...`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getProgressColor = (progress: number) => {
    if (progress === 100) return "success";
    if (progress >= 75) return "info";
    if (progress >= 50) return "warning";
    return "error";
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case "Pre-Onboarding":
        return "error";
      case "Onboarding":
        return "warning";
      case "Early Integration":
        return "info";
      case "Integration Period":
        return "primary";
      case "Established Employee":
        return "success";
      default:
        return "default";
    }
  };

  const getDaysEmployedCategory = (days: number) => {
    if (days < 7) return "New Hire";
    if (days < 30) return "First Month";
    if (days < 90) return "Learning Phase";
    if (days < 180) return "Growing";
    return "Experienced";
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <AwardIcon color="primary" sx={{ mr: 1 }} />
          Employee Milestones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track employee journey progress and upcoming milestone reviews (Auto-refreshes every 30s)
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && employees.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              Loading employee milestones...
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Employee Milestones Grid */}
      <Grid container spacing={3}>
        {employees.map((employee) => (
          <Grid item xs={12} md={6} key={employee.id}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                border: employee.progress < 50 ? `2px solid ${theme.palette.warning.main}` : "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <UserIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {employee.employeeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {employee.employeeId}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={employee.currentPhase}
                    color={getPhaseColor(employee.currentPhase) as any}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Journey Progress
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AwardIcon
                        color={getProgressColor(employee.progress) as any}
                        sx={{ fontSize: 16, mr: 0.5 }}
                      />
                      <Typography
                        variant="body2"
                        color={`${getProgressColor(employee.progress)}.main`}
                        fontWeight={500}
                      >
                        {employee.completedMilestones}/{employee.totalMilestones}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={employee.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: (() => {
                          const color = getProgressColor(employee.progress);
                          return theme.palette[color as 'success' | 'info' | 'warning' | 'error']?.main || theme.palette.primary.main;
                        })(),
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Join Date:</strong> {employee.joinDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Days Employed:</strong> {employee.daysEmployed} ({getDaysEmployedCategory(employee.daysEmployed)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Next Milestone:</strong> {employee.nextMilestone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    <strong>Due:</strong> {employee.nextMilestoneDate}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<EyeIcon />}
                  onClick={() => handleViewTimeline(employee.id)}
                  fullWidth
                >
                  View Full Timeline
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {employees.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <AwardIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No employee milestones found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Employee milestone tracking will appear here as employees progress through their journey.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MilestonesTab;